import React from 'react';
import { Link } from 'react-router-dom';
import {
  HomeOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { GetDecryptedString } from './encryption';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import i18next from 'i18next';

dayjs.extend(utc);

//system ID
export const systemID = '003';

//時間區間
export const DEFAULT_TIMEZONE = '8';

//刷新時間
export const refetchInterval = 1000 * 60 * 5;

// loading spin
export const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 55,
    }}
    spin
  />
);

//路由
export const routeList = [
  {
    path: '/',
    breadcrumbName: (
      <>
        <HomeOutlined />
        <span>{i18next.t('common.routes.home')}</span>
      </>
    ),
  },
  {
    path: '/filemanager',
    breadcrumbName: i18next.t('common.routes.fileManager'),
  },
  {
    path: '/filemanager/folders',
    breadcrumbName: i18next.t('common.routes.folder'),
  },
  {
    path: '/filemanager/folders/:id',
    breadcrumbName: i18next.t('common.routes.folder'),
  },
  // {
  //   path: '/projects',
  //   breadcrumbName: '專案資訊管理',
  // },
  {
    path: '/settings',
    breadcrumbName: i18next.t('common.routes.settings'),
  },
  {
    path: '/changepassword',
    breadcrumbName: i18next.t('common.routes.changePassword'),
  },
];

//抓取token
export const getToken = () => {
  return GetDecryptedString(window.sessionStorage.getItem('token'));
};

//抓取refreshToken
export const getRefreshToken = () => {
  return GetDecryptedString(window.sessionStorage.getItem('refreshToken'));
};

//麵包屑計算路由
export const itemRender = (route, params, routes) => {
  const last = routes.indexOf(route) === routes.length - 1;
  return last ? <span>{route.breadcrumbName}</span> : <Link to={route.path}>{route.breadcrumbName}</Link>;
};

export const getTimelinePeriod = (timeline = 'day') => {
  if (timeline === 'day' || timeline === 'week') {
    return 'quarter';
  } else if (timeline === 'month') {
    return 'daily';
  } else if (timeline === 'year') {
    return 'monthly';
  } else {
    return 'yearly';
  }
};

export const getTimelinePeriodForTarget = (timeline = 'day') => {
  if (timeline === 'day' || timeline === 'week') {
    return 'daily';
  } else if (timeline === 'month') {
    return 'monthly';
  } else if (timeline === 'year') {
    return 'yearly';
  } else {
    return 'yearly';
  }
};

export const dateOptions = [
  {
    label: '天',
    value: 'day',
  },
  {
    label: '週',
    value: 'week',
  },
  {
    label: '月',
    value: 'month',
  },
  {
    label: '年',
    value: 'year',
  },
  {
    label: '累積至今',
    value: 'accumulatedToNow',
  },
];

//計算累積值
export const calculateCumulationValue = (data = [], key = 'value') => {
  if (data?.length === 0 || data === null) return 0;
  const cumulationValue = data?.reduce?.(
    (accumulator, currentValue) => accumulator + (currentValue?.[key] || 0),
    0
  );
  return Number(cumulationValue?.toFixed(2));
};

//token過期時間 (30分)
export const expiresIn = 30 * 60 * 1000;

//編碼網址參數
export const codeParams = (data) => {
  let queryString = '';

  // 遍历数组，为每个对象构建查询字符串
  data.forEach((obj, index) => {
    Object.keys(obj).forEach((key) => {
      queryString += `conditions[${index}][${key}]=${encodeURIComponent(obj[key])}&`;
    });

    return queryString;
  });

  // 移除最后一个多余的"&"
  return queryString.slice(0, -1);
};

//解析網址參數
export const decodeParams = (queryString) => {
  // 解析查询字符串
  const params = new URLSearchParams(queryString);

  // 初始化结果数组
  const result = [];

  // 遍历所有参数
  for (const [key, value] of params) {
    // 使用正则表达式匹配索引和字段名
    const match = key.match(/conditions\[(\d+)\]\[(\w+)\]/);
    if (match) {
      const index = parseInt(match[1], 10);
      const field = match[2];

      // 确保数组长度
      while (index >= result.length) {
        result.push({});
      }

      // 添加值到对应的对象中
      result[index][field] = value;
    }
  }
  return result;
};

//清除使用者資訊
export const sessionClear = () => {
  window.localStorage.removeItem('user');
  window.localStorage.removeItem('token');
  window.localStorage.removeItem('google_token_expiration');
};

// 格式化時間
export const formatTimestamp = (timestamp) => {
  // 假設原始時間是 UTC 時間
  const utcTime = dayjs.utc(timestamp);
  
  // 轉換為用戶的本地時間
  const localTime = utcTime.local();
  
  // 格式化時間
  return localTime.format('YYYY-MM-DD HH:mm:ss');
};

export const getStringQAContent = (content) => {
  try {
    const json = JSON.parse(content);
    return json;
  } catch {
    return content;
  }
};

// 取得檔案類型的 label value list
export const getDocumentType = (value) => {
  const typeList = [
    {
      label: 'Markdown',
      value: 'md'
    },
    {
      label: 'Markdown', 
      value: 'markdown'
    },
    {
      label: 'Text',
      value: 'txt'
    },
    {
      label: 'Word',
      value: 'doc'
    },
    {
      label: 'Word',
      value: 'docx'
    },
    {
      label: 'Excel',
      value: 'xls'
    },
    {
      label: 'Excel',
      value: 'xlsx'
    },
    {
      label: 'PowerPoint',
      value: 'ppt'
    },
    {
      label: 'PowerPoint',
      value: 'pptx'
    },
    {
      label: 'PDF',
      value: 'pdf'
    },
    {
      label: 'Image',
      value: 'jpg'
    },
    {
      label: 'Image',
      value: 'jpeg'
    },
    {
      label: 'Image',
      value: 'png'
    },
    {
      label: 'Image',
      value: 'gif'
    }
  ];
  if (value) {
    const found = typeList.find(type => type.value === value);
    return found ? found.label : '';
  }
  return '';
};

export const extractFolderIdFromUrl = (url) => {
  try {
    // 匹配不同格式的 Google Drive URL 中的文件夾 ID
    // 格式1: https://drive.google.com/drive/folders/FOLDER_ID
    // 格式2: https://drive.google.com/drive/u/0/folders/FOLDER_ID
    // 格式3: https://drive.google.com/drive/my-drive/folders/FOLDER_ID
    // 格式4: https://drive.google.com/open?id=FOLDER_ID
    
    // 處理 /folders/ 格式
    const folderRegex = /\/folders\/([a-zA-Z0-9_-]+)/;
    const folderMatch = url.match(folderRegex);
    if (folderMatch && folderMatch[1]) {
      return folderMatch[1];
    }
    
    // 處理 open?id= 格式
    const idRegex = /[?&]id=([a-zA-Z0-9_-]+)/;
    const idMatch = url.match(idRegex);
    if (idMatch && idMatch[1]) {
      return idMatch[1];
    }
    
    return null;
  } catch (error) {
    console.error('提取資料夾ID失敗:', error);
    return null;
  }
};

export function extractGoogleDriveId(url) {
  if (!url) return null;
  let id = null;
  const patterns = [
    /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/,       // For /file/d/ID/view or /file/d/ID/edit
    /drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/,        // For /open?id=ID
    /drive\.google\.com\/uc\?id=([a-zA-Z0-9_-]+)/,           // For /uc?id=ID&export=download etc.
    /docs\.google\.com\/(?:document|spreadsheets|presentation)\/d\/([a-zA-Z0-9_-]+)/ // For docs/document/d/ID/edit etc.
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      id = match[1];
      break;
    }
  }
  return id;
}

export function getFileExtension(filename) {
  if (!filename || typeof filename !== 'string') return '';
  return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2).toLowerCase(); // +2 to get extension without dot
}

export function getMimeTypeFromFileName(fileName) {
  if (!fileName) return 'application/octet-stream';
  
  const extension = getFileExtension(fileName);
  if (!extension) return 'application/octet-stream';
  
  const mimeTypeMap = {
    // 音頻視頻檔案
    'mp3': 'audio/mpeg',
    'aac': 'audio/aac',
    'm4a': 'audio/x-m4a',
    'mp4': 'video/mp4',
    'mkv': 'video/x-matroska',
    'wav': 'audio/wav',
    'ogg': 'audio/ogg',
    'mov': 'video/quicktime',
    'avi': 'video/x-msvideo',
    
    // 文件檔案
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'pdf': 'application/pdf',
    'txt': 'text/plain'
  };
  
  return mimeTypeMap[extension.toLowerCase()] || 'application/octet-stream';
}


/**
 * 解析 Google Calendar 事件 ID，提取基礎部分（去除日期時間後綴）
 * @param {string} eventId - 完整的事件 ID，如 "7034rf6ekems44ej9apnghn7pc_20250505T023000Z"
 * @returns {string} 基礎事件 ID，如 "7034rf6ekems44ej9apnghn7pc"
 */
export function parseRecurringEventId(eventId) {
  if (!eventId || typeof eventId !== 'string') return '';
  
  // 週期性事件的 ID 格式通常是 baseId_dateTimeStamp
  // 例如：7034rf6ekems44ej9apnghn7pc_20250505T023000Z
  const underscoreIndex = eventId.lastIndexOf('_');
  if (underscoreIndex === -1) {
    return eventId; // 非週期性事件，返回原 ID
  }
  
  const afterUnderscore = eventId.substring(underscoreIndex + 1);
  // 檢查是否符合日期時間格式 (YYYYMMDDTHHMMSSZ)
  const dateTimePattern = /^\d{8}T\d{6}Z$/;
  if (dateTimePattern.test(afterUnderscore)) {
    return eventId.substring(0, underscoreIndex);
  }
  
  return eventId; // 不符合週期性格式，返回原 ID
}